// @flow
import React, { PureComponent } from "react";
import { connect, Dispatch } from "react-redux";
import MapViewer from "components/MapViewer";
import {
    type MapLocationInfo,
    type SearchResultDTO,
    type MapProviderConfig,
    type RasterLocation,
} from "data/types";
import {
    ParseLocation,
    ParseArea,
    ParseRaster,
} from "components/MapViewer/utils";
import Constants, { type ConstantsEnum } from "data/constants";
import Status from "components/Status";
import * as s from "data/reducers/selectors";
import NoData from "components/NoData";
import Button from "react-bootstrap/lib/Button";
import loc from "i18next";

type Props = {
    /** Redux dispatch */
    dispatch: Dispatch,
    /** fetch status */
    status: ConstantsEnum,
    /** search results */
    result: ?SearchResultDTO,
    /** callback when we know number of geo locations */
    onCountOfGeoResults: (count: number) => void,
    /** callback to load next page
     * @param {number} pageNum which page number to load
     */
    loadMoreRows: (pageNum: number) => Promise<SearchResultDTO>,
    /** custom mapProvider to use */
    mapProvider?: MapProviderConfig,
    /** callback when user clicks an empty space on the map (only when useMarker=false & mapProvider.onClick exists) */
    onClick?: (location: RasterLocation) => void,
};

type State = {
    locations: ?Array<MapLocationInfo>,
    pageNum: number,
    totalLoaded: number,
    isLoadingNextPage: boolean,
    hasMorePages: boolean,
};

export class SearchResultMap extends PureComponent<Props, State> {
    state = {
        locations: null,
        pageNum: 0,
        totalLoaded: 0,
        isLoadingNextPage: false,
        hasMorePages: false,
    };

    componentDidMount() {
        const { status, result } = this.props;
        if (status !== Constants.OK) return;
        else this._firstPage(result);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        /* istanbul ignore else */
        if (
            this.props.status !== prevProps.status &&
            this.props.status === Constants.OK
        ) {
            this._firstPage(this.props.result);
        }
        /* istanbul ignore else */
        if (
            this.state.locations !== prevState.locations &&
            typeof this.props.onCountOfGeoResults === "function"
        ) {
            this.props.onCountOfGeoResults(this.state.locations.length);
        }
    }

    _firstPage = (result: SearchResultDTO) => {
        const locations = this._getLocationsFromResult(result);
        this.setState({
            locations,
            hasMorePages: result.totalCount > result.pageSize,
        });
    };

    _getLocationsFromResult = (result: SearchResultDTO) => {
        let locations: Array<MapLocationInfo> = [];
        let totalLoaded = this.state.totalLoaded;
        const onlyRaster =
            this.props.mapProvider && this.props.mapProvider.isRaster === true;
        Object.keys(result.rows).forEach((rowKey) => {
            totalLoaded++;
            const row = result.rows[rowKey];
            this._getLocations(row).forEach((attr) => {
                let location: MapLocationInfo = {
                    latitude: null,
                    longitude: null,
                    label: row.title,
                    attribute: attr.caption,
                    row,
                    itemUri: row.itemUri,
                    thumbnail: null,
                    icon: (attr.value && attr.value.marker) || null,
                };
                switch (attr.format) {
                    case "Text_GeoPoint":
                        if (onlyRaster) location = null;
                        else {
                            const point = ParseLocation(
                                attr.value.value || attr.value
                            );
                            location.latitude = point.latitude;
                            location.longitude = point.longitude;
                        }
                        break;
                    case "Text_GeoArea":
                        if (onlyRaster) location = null;
                        else
                            location.area = ParseArea(
                                attr.value.value || attr.value
                            );
                        break;
                    case "Text_GeoRaster":
                        if (onlyRaster)
                            location = Object.assign(
                                {},
                                location,
                                ParseRaster(attr.value.value || attr.value)
                            );
                        else location = null;
                        break;
                    default:
                        location = null;
                        console.warn(
                            "Unsupported row attribute format " + attr.format
                        );
                }
                if (location != null) locations.push(location);
            });
        });
        this.setState({ totalLoaded });
        return locations;
    };

    /** find all attributes with type Text_GeoPoint or Text_GeoArea */
    _getLocations = (row: Object) =>
        row.attributes.filter(
            (a) => a.format && a.format.startsWith("Text_Geo")
        );

    // TODO should really be loaded by Redux dispatch
    _loadNextPage = () => {
        this.setState({ isLoadingNextPage: true });
        this.props.loadMoreRows(this.state.pageNum + 1).then((result) => {
            const nextLocations = this._getLocationsFromResult(result);
            this.setState({
                locations: this.state.locations.concat(nextLocations),
                pageNum: this.state.pageNum + 1,
                hasMorePages:
                    this.state.totalLoaded < this.props.result.totalCount,
                isLoadingNextPage: false,
            });
        });
    };

    render() {
        const { status } = this.props;
        if (status !== Constants.OK) return <Status status={status} />;

        // #51511 Raster Map should always show even if no geo results were found
        const isRaster = (this.props.mapProvider?.isRaster ?? false) !== false;

        if ((this.state.locations?.length ?? 0) === 0 && !isRaster) {
            return <NoData locContext="map" />;
        }

        return (
            <>
                {this.state.hasMorePages && (
                    <Button
                        disabled={this.state.isLoadingNextPage}
                        onClick={this._loadNextPage}
                        style={{
                            position: "absolute",
                            display: "inline",
                            zIndex: 1000,
                            bottom: 20,
                            left: 20,
                        }}
                    >
                        {loc.t("location.loadNextPage") + " "}
                        {this.state.isLoadingNextPage && (
                            <Status inline status={Constants.LOADING} />
                        )}
                    </Button>
                )}
                <MapViewer.WithContext
                    mapProvider={this.props.mapProvider}
                    useMarker={false}
                    locations={this.state.locations}
                    onClick={this.props.onClick}
                />
            </>
        );
    }
}

// TODO move to a container
const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    status: s.searchStatusSelector(state),
    result:
        s.searchStatusSelector(state) === Constants.OK
            ? s.searchResultSelector(state)
            : undefined,
    ...ownProps,
});

export default connect(mapStateToProps)(SearchResultMap);
